import { motion } from "framer-motion";
import {
  Home,
  HardHat,
  ShieldPlus,
  PartyPopper,
  Sofa,
  Bug,
  ArrowUpRight,
} from "lucide-react";
import { WA_LINK } from "@/lib/contact";

const EASE = [0.16, 1, 0.3, 1];

const SERVICES = [
  {
    icon: Home,
    title: "Complete Home Deep Cleaning",
    text: "Tailored for Houses, Villas, Flats, Offices, and Showrooms.",
  },
  {
    icon: HardHat,
    title: "Post-Construction Deep Cleaning",
    text: "Removing dust, debris, and marks after building work.",
  },
  {
    icon: ShieldPlus,
    title: "Medical Grade Deep Cleaning",
    text: "High-level sanitization for maximum hygiene.",
  },
  {
    icon: PartyPopper,
    title: "Pre-Function Deep Cleaning",
    text: "Pristine prep before weddings and events.",
  },
  {
    icon: Bug,
    title: "Pest Control for Termites",
    text: "Targeted treatments for long-term protection.",
  },
  {
    icon: Sofa,
    title: "Shampoo Washing",
    text: "Professional extraction for Sofas, Beds, Carpets, etc.",
    big: true,
    highlight: true,
    img: "/assets/work-sofa.jpg",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      className="relative py-24 lg:py-36 bg-gradient-to-b from-[#FAFCFA] to-[#EFF8E8]/60"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14 lg:mb-20">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: EASE }}
              className="font-mono-accent text-xs font-semibold uppercase tracking-[0.28em] text-[#6CBE45] mb-5"
            >
              Our Services
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.85, delay: 0.1, ease: EASE }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] max-w-2xl"
            >
              Six ways we make spaces{" "}
              <span className="font-serif-accent italic font-semibold text-[#5BA357]">
                spotless
              </span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="text-[#4A5A5E] max-w-sm text-sm sm:text-base leading-relaxed"
          >
            Every service ends with a supervised quality sign-off — and photo
            proof sent straight to your WhatsApp.
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.12, ease: EASE }}
              className={`group relative overflow-hidden rounded-2xl bg-white border p-8 hover:-translate-y-1.5 transition-[transform,border-color,box-shadow] duration-500 ${
                s.highlight
                  ? "border-[#D4AF37]/70 shadow-[0_0_40px_-6px_rgba(212,175,55,0.45)] hover:shadow-[0_0_55px_-4px_rgba(212,175,55,0.6)]"
                  : "border-[#6CBE45]/12 hover:border-[#D4AF37]/60 hover:shadow-[0_24px_60px_-18px_rgba(74,143,42,0.3)]"
              } ${
                s.big ? "sm:col-span-2 lg:col-span-1 lg:row-span-2 flex flex-col" : ""
              }`}
              data-testid={`service-card-${i + 1}`}
            >
              {s.highlight && (
                <span
                  className="absolute top-4 right-4 z-10 rounded-full bg-[#D4AF37] text-[#0F1A1C] text-[10px] font-mono-accent font-bold uppercase tracking-[0.18em] px-3.5 py-1.5 shadow-lg"
                  data-testid="service-premium-badge"
                >
                  Premium Service
                </span>
              )}
              {s.big && (
                <div className="relative -mx-8 -mt-8 mb-7 h-52 overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1100ms] ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent" />
                </div>
              )}
              <span className="w-12 h-12 rounded-xl bg-[#EFF8E8] group-hover:bg-[#FDFBF2] flex items-center justify-center transition-colors duration-500">
                <s.icon className="w-6 h-6 text-[#6CBE45] group-hover:text-[#D4AF37] transition-colors duration-500" />
              </span>
              <h3 className="mt-6 text-lg sm:text-xl font-semibold tracking-tight">
                {s.title}
              </h3>
              <p className="mt-3 text-sm sm:text-base text-[#4A5A5E] leading-relaxed flex-1">
                {s.text}
              </p>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`service-book-link-${i + 1}`}
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A8F2A] hover:text-[#B89628] transition-colors duration-300"
              >
                Book this service
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </a>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
